const SPX_HEADER_TAB_MOBILE_MEDIA = window.matchMedia('(max-width: 1023px)');

/** @type {WeakMap<Element, MegaTab>} */
const instances = new WeakMap();

class MegaTab {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    /** @type {HTMLElement} */
    this.container = container;
    /** @type {HTMLElement | null} */
    this.boxTab = container.querySelector('.box-tab');
    /** @type {HTMLElement | null} */
    this.boxPanel = container.querySelector('.box-panel');
    /** @type {HTMLElement[]} */
    this.tabs = Array.from(container.querySelectorAll('.box-tab .tab-title'));
    /** @type {HTMLElement[]} */
    this.panels = Array.from(container.querySelectorAll('.box-panel .tab-content'));

    if (!this.boxTab || !this.boxPanel || !this.tabs.length || !this.panels.length) return;

    /** @type {'mobile' | 'desktop' | null} */
    this.mode = null;
    /** @type {AbortController | null} */
    this.modeController = null;
    /** @type {{ details: HTMLDetailsElement; title: HTMLElement; panel: HTMLElement }[]} */
    this.accordionItems = [];
    /** @type {MutationObserver | null} */
    this.menuOpenObserver = null;

    this.applyMode();
    this.observeMenuOpen();
    SPX_HEADER_TAB_MOBILE_MEDIA.addEventListener('change', this.applyMode);
  }

  observeMenuOpen() {
    const menuItem = this.container.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
    if (!(menuItem instanceof HTMLElement)) return;

    this.menuOpenObserver = new MutationObserver(() => {
      if (menuItem.getAttribute('aria-expanded') === 'true') {
        this.resetToDefault();
      }
    });
    this.menuOpenObserver.observe(menuItem, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  resetToDefault() {
    if (this.mode === 'mobile') {
      this.accordionItems.forEach(({ details, title, panel }, index) => {
        const isDefault = index === 0;
        details.open = isDefault;
        title.toggleAttribute('active', isDefault);
        panel.toggleAttribute('active', isDefault);
      });
      this.updateSubmenuHeight();
      return;
    }

    const firstTab = this.tabs[0];
    if (firstTab) this.setActive(firstTab);
  }

  applyMode = () => {
    const nextMode = SPX_HEADER_TAB_MOBILE_MEDIA.matches ? 'mobile' : 'desktop';
    if (this.mode === nextMode) return;

    if (this.modeController) this.modeController.abort();
    this.modeController = new AbortController();

    if (nextMode === 'mobile') {
      this.teardownDesktop();
      this.setupMobile();
    } else {
      this.teardownMobile();
      this.setupDesktop();
    }

    this.mode = nextMode;
  };

  setupDesktop() {
    this.restoreFromAccordion();
    if (this.boxPanel) this.boxPanel.style.display = '';

    this.tabs = Array.from(this.container.querySelectorAll('.box-tab .tab-title'));
    this.panels = Array.from(this.container.querySelectorAll('.box-panel .tab-content'));
    if (!this.tabs.length || !this.panels.length) return;

    const signal = this.modeController?.signal;
    if (!signal) return;
    this.tabs.forEach((tab) => {
      tab.addEventListener('mouseenter', () => this.setActive(tab), { signal });
    });

    const firstTab = this.tabs[0];
    if (firstTab) this.setActive(firstTab);
  }

  teardownDesktop() {
    this.tabs.forEach((t) => t.removeAttribute('active'));
    this.panels.forEach((p) => p.removeAttribute('active'));
  }

  setupMobile() {
    this.buildAccordion();
    if (this.boxPanel) this.boxPanel.style.display = 'none';

    const signal = this.modeController?.signal;
    if (!signal) return;
    this.accordionItems.forEach(({ details, title, panel }) => {
      details.addEventListener(
        'toggle',
        () => {
          if (details.open) {
            this.accordionItems.forEach((item) => {
              if (item.details === details) return;
              item.details.open = false;
              item.title.removeAttribute('active');
              item.panel.removeAttribute('active');
            });
            title.setAttribute('active', '');
            panel.setAttribute('active', '');
          } else {
            title.removeAttribute('active');
            panel.removeAttribute('active');
          }

          this.updateSubmenuHeight();
        },
        { signal }
      );
    });

    const first = this.accordionItems[0];
    if (!first) return;
    first.details.open = true;
    first.title.setAttribute('active', '');
    first.panel.setAttribute('active', '');
    this.updateSubmenuHeight();
  }

  teardownMobile() {
    this.accordionItems.forEach(({ title, panel }) => {
      title.removeAttribute('active');
      panel.removeAttribute('active');
    });
  }

  buildAccordion() {
    if (!this.boxTab || !this.boxPanel) return;
    if (this.accordionItems.length) return;

    this.tabs = Array.from(this.container.querySelectorAll('.box-tab .tab-title'));
    this.panels = Array.from(this.container.querySelectorAll('.box-panel .tab-content'));
    const count = Math.min(this.tabs.length, this.panels.length);

    for (let i = 0; i < count; i++) {
      const title = this.tabs[i];
      const panel = this.panels[i];
      if (!(title instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;

      const details = document.createElement('details');
      const summary = document.createElement('summary');
      const content = document.createElement('div');

      details.className = 'spx-mega-tab-accordion';
      summary.className = 'spx-mega-tab-accordion__summary';
      content.className = 'spx-mega-tab-accordion__content';

      title.parentElement?.insertBefore(details, title);
      summary.appendChild(title);
      content.appendChild(panel);
      details.appendChild(summary);
      details.appendChild(content);

      panel.removeAttribute('active');
      title.removeAttribute('active');

      this.accordionItems.push({ details, title, panel });
    }
  }

  restoreFromAccordion() {
    if (!this.boxTab || !this.boxPanel) return;
    if (!this.accordionItems.length) return;

    this.accordionItems.forEach(({ details, title, panel }) => {
      details.parentElement?.insertBefore(title, details);
      this.boxPanel?.appendChild(panel);
      details.remove();
    });

    this.accordionItems = [];
  }

  /**
   * @param {HTMLElement} activeTab
   */
  setActive(activeTab) {
    this.tabs.forEach((t) => t.removeAttribute('active'));
    this.panels.forEach((p) => p.removeAttribute('active'));

    activeTab.setAttribute('active', '');
    const index = this.tabs.indexOf(activeTab);
    if (index !== -1 && this.panels[index]) {
      this.panels[index].setAttribute('active', '');
    }

    // 重新计算 submenu 高度
    this.updateSubmenuHeight();
  }

  updateSubmenuHeight() {
    const submenu = this.container.closest('.menu-list__submenu');
    if (!(submenu instanceof HTMLElement)) return;

    const headerMenu = this.container.closest('header-menu');
    const applyHeaderMenuHeight = () => {
      if (!(headerMenu instanceof HTMLElement)) return;
      const submenuHeight = submenu.offsetHeight;
      headerMenu.style.setProperty('--submenu-height', `${submenuHeight}px`);
    };

    const emitSubmenuContentChange = () => {
      submenu.dispatchEvent(new CustomEvent('spx:submenu-content-change', { bubbles: true }));
    };

    // Tab 内容切换时（图片/字体延迟）可能导致高度晚变化，做多次轻量重算。
    requestAnimationFrame(() => {
      applyHeaderMenuHeight();
      emitSubmenuContentChange();
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyHeaderMenuHeight();
        emitSubmenuContentChange();
      });
    });

    window.setTimeout(() => {
      applyHeaderMenuHeight();
      emitSubmenuContentChange();
    }, 220);
  }
}

const initMegaTabs = () => {
  document.querySelectorAll('mega-tab').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (instances.has(el)) return;
    instances.set(el, new MegaTab(el));
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaTabs, { once: true });
} else {
  initMegaTabs();
}